'use client';

import { X } from 'lucide-react';
import { clsx } from 'clsx';
import type { ReactNode } from 'react';

export interface SelectionBarProps {
  count: number;
  onClear: () => void;
  children: ReactNode;
  /** Mobil über der Tabbar positionieren (Standard) oder knapp über dem Rand. */
  aboveBottomNav?: boolean;
  ariaLabel?: string;
  /**
   * Erzwingt die Sichtbarkeit auch ohne Auswahl (count 0) — z. B. im
   * Auswahlmodus, damit der Nutzer die verfügbaren Sammelaktionen sieht.
   * Ohne `open` verhält sich die Leiste wie bisher (nur bei count > 0).
   */
  open?: boolean;
  /** Substantiv hinter der Zahl (Standard: „gewählt"). */
  noun?: string;
  /** Optionaler Ersatz-Inhalt für den Zähler-Text (überschreibt count + noun). */
  label?: ReactNode;
}

/**
 * Geteilte, responsive Aktionsleiste für Mehrfachauswahl — einheitlich für
 * Favoriten, Belege und Projekte. Fixiert unten: mobil über die volle Breite
 * (über der Tabbar), auf Desktop zentriert als Pille. Immer dunkler
 * Hintergrund (`bg-overlay`, theme-unabhängig), runder Schließen-Button oben
 * rechts. Die
 * jeweiligen Aktions-Buttons kommen als `children` von der Seite.
 */
export function SelectionBar({
  count,
  onClear,
  children,
  aboveBottomNav = true,
  ariaLabel = 'Auswahl-Aktionen',
  open,
  noun = 'gewählt',
  label,
}: SelectionBarProps) {
  if (!open && count === 0) return null;

  return (
    <div
      role="region"
      aria-label={ariaLabel}
      className={clsx(
        'fixed z-50 inset-x-3 md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:max-w-[calc(100vw-2rem)]',
        'bg-overlay/90 text-white rounded-2xl shadow-2xl px-2 py-2 animate-in slide-in-from-bottom-2',
        aboveBottomNav
          ? 'bottom-[calc(5.5rem+env(safe-area-inset-bottom))] md:bottom-6'
          : 'bottom-[calc(1rem+env(safe-area-inset-bottom))] md:bottom-6',
      )}
    >
      <button
        type="button"
        onClick={onClear}
        aria-label="Auswahl aufheben"
        title="Auswahl aufheben"
        className="absolute -top-3 -right-1 w-7 h-7 rounded-full bg-overlay text-white shadow-lg ring-2 ring-white/90 flex items-center justify-center hover:bg-overlay-hover transition-colors"
      >
        <X size={14} />
      </button>
      <div className="flex flex-col gap-2">
        <span className="text-xs font-medium px-1 whitespace-nowrap">
          {label ?? (
            <>
              <b className="font-bold tabular-nums">{count}</b> {noun}
            </>
          )}
        </span>
        <div className="flex flex-wrap items-center gap-2 justify-between">
          {children}
        </div>
      </div>
    </div>
  );
}
